import {
    AccountEnvironment,
    UserRepresentation,
    UserProfileAttributeMetadata,
    getPersonalInfo,
    getSupportedLocales,
    savePersonalInfo,
    useEnvironment,
    usePromise,
} from "@keycloak/keycloak-account-ui";
import { Page, TextField, SelectField, TextAreaField, Button, TextSkeleton, RoundedSkeleton } from "../components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";
import { environment } from "../environment";
import { showPromiseToast } from "../utils";
import {
    canUseUpdateEmailAction,
    isImmutablePersonalInfoField,
    sanitizePersonalInfoForSave,
    withImmutablePersonalInfoMetadata,
} from "../utils/personalInfo";
import { notifyAccountReady } from "../utils/startupOverlay";

export const PersonalInfo = () => {
    const { t } = useTranslation();
    const context = useEnvironment<AccountEnvironment>();
    const [personalInfo, setPersonalInfo] = useState<UserRepresentation>();
    const [initialPersonalInfo, setInitialPersonalInfo] = useState<UserRepresentation>();
    const [supportedLocales, setSupportedLocales] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const canUpdateEmail = canUseUpdateEmailAction(context.environment.features);

    usePromise(
        async (signal) => {
            const [data, locales] = await Promise.all([
                getPersonalInfo({ signal, context }),
                getSupportedLocales({ signal, context }).catch(() => []),
            ]);
            setSupportedLocales(locales);
            setIsDataLoading(false);
            return withImmutablePersonalInfoMetadata(data);
        },
        (data) => {
            setInitialPersonalInfo(data);
            setPersonalInfo(data);
        }
    );

    useEffect(() => {
        if (!isDataLoading) {
            notifyAccountReady();
        }
    }, [isDataLoading]);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);

        const nextPersonalInfo = sanitizePersonalInfoForSave(personalInfo, initialPersonalInfo);

        if (!nextPersonalInfo) {
            setIsLoading(false);
            return;
        }

        const savePromise = savePersonalInfo(context, nextPersonalInfo);
        const selectedLocale = nextPersonalInfo.attributes?.locale?.[0];

        showPromiseToast(savePromise, {
            loading: t("personalInfo.save.loading", "Saving personal information..."),
            success: t("personalInfo.save.success", "Personal information updated successfully"),
            error: t("personalInfo.save.error", "Failed to update personal information")
        }).then(() => {
            if (selectedLocale && selectedLocale !== environment.locale) {
                window.location.reload();
            }
        }).finally(() => {
            setIsLoading(false);
        });
    };

    // Get user profile attributes from metadata
    const userAttributes = personalInfo?.userProfileMetadata?.attributes || [];
    const orderedUserAttributes = [
        ...userAttributes.filter((attribute: UserProfileAttributeMetadata) => attribute.name !== "locale"),
        ...userAttributes.filter((attribute: UserProfileAttributeMetadata) => attribute.name === "locale"),
    ];

    const getLanguageDisplayName = (locale: string) => {
        const normalizedLocale = locale.toLowerCase();

        if (normalizedLocale === "ru") {
            return t("locale_ru", "Русский");
        }

        if (normalizedLocale === "en") {
            return t("locale_en", "English");
        }

        return locale.toUpperCase();
    };

    const humanizeAttributeName = (attributeName: string) =>
        attributeName
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str: string) => str.toUpperCase())
            .trim();

    const getTemplateKey = (value?: string) =>
        value?.startsWith("${") && value.endsWith("}") ? value.slice(2, -1) : undefined;

    const getDefaultDisplayName = (attributeName: string) => {
        switch (attributeName) {
            case "username":
                return "Username";
            case "email":
                return "Email";
            case "firstName":
                return "First name";
            case "lastName":
                return "Last name";
            case "locale":
                return "Interface language";
            default:
                return humanizeAttributeName(attributeName);
        }
    };

    // Helper function to get field value
    const getFieldValue = (attributeName: string) => {
        if (attributeName === 'username') return personalInfo?.username || '';
        if (attributeName === 'email') return personalInfo?.email || '';
        if (attributeName === 'firstName') return personalInfo?.firstName || '';
        if (attributeName === 'lastName') return personalInfo?.lastName || '';

        // For custom attributes, check the attributes object
        return personalInfo?.attributes?.[attributeName]?.[0] || '';
    };

    // Helper function to update field value
    const updateFieldValue = (attributeName: string, value: string) => {
        if (isImmutablePersonalInfoField(attributeName)) {
            return;
        }

        if (attributeName === 'firstName') {
            setPersonalInfo({ ...personalInfo, firstName: value });
        } else if (attributeName === 'lastName') {
            setPersonalInfo({ ...personalInfo, lastName: value });
        } else {
            // For custom attributes
            const newAttributes = { ...personalInfo?.attributes };
            newAttributes[attributeName] = [value];
            setPersonalInfo({ ...personalInfo, attributes: newAttributes });
        }
    };

    // Helper function to get input type based on attribute annotations and validators
    const getInputType = (attribute: UserProfileAttributeMetadata) => {
        if (attribute.name === "locale" && supportedLocales.length > 0) {
            return "select";
        }

        // Check for explicit input type annotation
        if (attribute.annotations?.inputType) {
            return attribute.annotations.inputType;
        }

        // Check validators for specific types
        if (attribute.name === 'email' || attribute.validators?.email) return 'email';
        if (attribute.validators?.uri || attribute.name.toLowerCase().includes('url') || attribute.name.toLowerCase().includes('website')) return 'url';
        if (attribute.name.toLowerCase().includes('phone') || attribute.name.toLowerCase().includes('mobile') || attribute.name.toLowerCase().includes('tel')) return 'tel';
        if (attribute.name.toLowerCase().includes('number') || attribute.name.toLowerCase().includes('age') || attribute.validators?.integer || attribute.validators?.double) return 'number';
        if (attribute.name.toLowerCase().includes('password')) return 'password';
        if (attribute.name.toLowerCase().includes('search')) return 'search';

        // Check for options validator (indicates select field)
        if (attribute.validators?.options) return 'select';

        // Check for textarea annotation or long text fields
        if (attribute.annotations?.inputType === 'textarea' ||
            attribute.name.toLowerCase().includes('description') ||
            attribute.name.toLowerCase().includes('bio') ||
            attribute.name.toLowerCase().includes('about') ||
            attribute.name.toLowerCase().includes('comment') ||
            attribute.name.toLowerCase().includes('note')) {
            return 'textarea';
        }

        return 'text';
    };

    // Helper function to get options for select fields
    const getSelectOptions = (attribute: UserProfileAttributeMetadata) => {
        if (attribute.name === "locale" && supportedLocales.length > 0) {
            return [...supportedLocales]
                .map((locale) => ({
                    value: locale,
                    label: t(`locale_${locale}`, getLanguageDisplayName(locale)),
                }))
                .sort((a, b) => a.label.localeCompare(b.label, environment.locale));
        }

        if (attribute.validators?.options?.options) {
            return attribute.validators.options.options.map((option: string) => ({
                value: option,
                label: option
            }));
        }
        return [];
    };

    // Helper function to get validation attributes
    const getValidationAttributes = (attribute: UserProfileAttributeMetadata) => {
        const validationAttrs: any = {};

        if (attribute.validators?.length) {
            if (attribute.validators.length.min) {
                validationAttrs.minLength = parseInt(attribute.validators.length.min);
            }
            if (attribute.validators.length.max) {
                validationAttrs.maxLength = parseInt(attribute.validators.length.max);
            }
        }

        return validationAttrs;
    };

    // Helper function to get proper display name
    const getDisplayName = (attribute: UserProfileAttributeMetadata) => {
        if (attribute.name === "locale") {
            return t("personalInfo.interfaceLanguage", "Interface language");
        }

        const templateKey = getTemplateKey(attribute.displayName);

        if (templateKey) {
            return t(templateKey, getDefaultDisplayName(attribute.name));
        }

        // Use displayName if available and not a template
        if (attribute.displayName && !attribute.displayName.includes('${')) {
            return attribute.displayName;
        }

        return t(attribute.name, getDefaultDisplayName(attribute.name));
    };

    // Helper function to render a form field
    const renderFormField = (attribute: UserProfileAttributeMetadata) => {
        const fieldValue = getFieldValue(attribute.name);
        const isEmailField = attribute.name === "email";
        const isReadOnly = attribute.readOnly || isImmutablePersonalInfoField(attribute.name);
        const isDisabled = isEmailField;
        const inputType = getInputType(attribute);
        const displayName = getDisplayName(attribute);
        const validationAttrs = getValidationAttributes(attribute);
        const renderEmailAction = () =>
            isEmailField && canUpdateEmail ? (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-4">
                    <div className="hidden md:block" />
                    <div className="md:col-span-2">
                        <Button
                            type="button"
                            variant="outline"
                            leftIcon={<Mail className="h-4 w-4" />}
                            fullWidth={true}
                            className="sm:w-auto"
                            onClick={() => context.keycloak.login({ action: "UPDATE_EMAIL" })}
                        >
                            {t("updateEmail", "Update email")}
                        </Button>
                    </div>
                </div>
            ) : null;

        // Render select field for options-based attributes
        if (inputType === 'select') {
            const options = getSelectOptions(attribute);
            return (
                <SelectField
                    key={attribute.name}
                    label={displayName}
                    id={attribute.name}
                    name={attribute.name}
                    value={fieldValue}
                    options={options}
                    required={attribute.required}
                    readOnly={isReadOnly}
                    disabled={isDisabled}
                    placeholder={!attribute.required ? t("selectOne", "Select an option") : undefined}
                    onChange={(e) => updateFieldValue(attribute.name, e.target.value)}
                    layout="horizontal"
                />
            );
        }

        // Render textarea field for long text content
        if (inputType === 'textarea') {
            return (
                <div key={attribute.name} className="space-y-3">
                    <TextAreaField
                        label={displayName}
                        id={attribute.name}
                        name={attribute.name}
                        value={fieldValue}
                        required={attribute.required}
                        readOnly={isReadOnly}
                        disabled={isDisabled}
                        rows={4}
                        placeholder={`Enter ${displayName.toLowerCase()}...`}
                        onChange={(e) => updateFieldValue(attribute.name, e.target.value)}
                        layout="horizontal"
                        {...validationAttrs}
                    />
                    {renderEmailAction()}
                </div>
            );
        }

        // Render regular text field
        return (
            <div key={attribute.name} className="space-y-3">
                <TextField
                    label={displayName}
                    type={inputType as any}
                    id={attribute.name}
                    name={attribute.name}
                    value={fieldValue}
                    required={attribute.required}
                    readOnly={isReadOnly}
                    disabled={isDisabled}
                    onChange={(e) => updateFieldValue(attribute.name, e.target.value)}
                    layout="horizontal"
                    {...validationAttrs}
                />
                {renderEmailAction()}
            </div>
        );
    };

    return (
        <Page
            title={t("personalInfo.title", "Personal info")}
            description={t("personalInfo.description", "Manage your basic information")}
            className="mt-3 p-3 sm:mt-4 sm:p-4"
        >
            <div className="space-y-4 md:space-y-6">

                {/* General Section */}
                <div id="general" className="rounded-2xl border border-border-primary p-4 sm:p-6 shadow-sm">
                    {isDataLoading ? (
                        <>
                            {/* Form Skeleton */}
                            <div className="space-y-6">
                                {/* Username Field Skeleton */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                    <div className="md:col-span-1 pt-2">
                                        <TextSkeleton width="80px" height="14px" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <RoundedSkeleton height="42px" className="w-full" />
                                    </div>
                                </div>

                                {/* Email Field Skeleton */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                    <div className="md:col-span-1 pt-2">
                                        <TextSkeleton width="50px" height="14px" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <RoundedSkeleton height="42px" className="w-full" />
                                    </div>
                                </div>

                                {/* First Name Field Skeleton */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                    <div className="md:col-span-1 pt-2">
                                        <TextSkeleton width="85px" height="14px" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <RoundedSkeleton height="42px" className="w-full" />
                                    </div>
                                </div>

                                {/* Last Name Field Skeleton */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                    <div className="md:col-span-1 pt-2">
                                        <TextSkeleton width="80px" height="14px" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <RoundedSkeleton height="42px" className="w-full" />
                                    </div>
                                </div>

                                {/* Action Buttons Skeleton */}
                                <div className="flex items-center justify-between pt-6 border-t border-border-primary">
                                    <div className="flex items-center">
                                        <RoundedSkeleton width="60px" height="42px" />
                                    </div>
                                    <div className="text-xs">
                                        <TextSkeleton width="120px" height="12px" />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Real Form */}
                            <form onSubmit={onSubmit} className="space-y-4 md:space-y-6">
                                {/* Dynamic Fields based on User Profile Configuration */}
                                {orderedUserAttributes.length > 0 ? (
                                    orderedUserAttributes.map((attribute: UserProfileAttributeMetadata) => renderFormField(attribute))
                                ) : (
                                    // Fallback to basic fields if no metadata available
                                    <>
                                        {/* Username Field */}
                                        <TextField
                                            label={t("username", "Username")}
                                            type="text"
                                            id="username"
                                            name="username"
                                            value={personalInfo?.username || ""}
                                            required
                                            readOnly
                                            layout="horizontal"
                                        />

                                        {/* Email Field */}
                                        <TextField
                                            label={t("email", "Email")}
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={personalInfo?.email || ""}
                                            required
                                            readOnly
                                            disabled
                                            layout="horizontal"
                                        />
                                        {canUpdateEmail && (
                                            <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-4">
                                                <div className="hidden md:block" />
                                                <div className="md:col-span-2">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        leftIcon={<Mail className="h-4 w-4" />}
                                                        fullWidth={true}
                                                        className="sm:w-auto"
                                                        onClick={() => context.keycloak.login({ action: "UPDATE_EMAIL" })}
                                                    >
                                                        {t("updateEmail", "Update email")}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* First Name Field */}
                                        <TextField
                                            label={t("firstName", "First name")}
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            value={personalInfo?.firstName || ""}
                                            required
                                            onChange={(e) => setPersonalInfo({ ...personalInfo, firstName: e.target.value })}
                                            layout="horizontal"
                                        />

                                        {/* Last Name Field */}
                                        <TextField
                                            label={t("lastName", "Last name")}
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            value={personalInfo?.lastName || ""}
                                            required
                                            onChange={(e) => setPersonalInfo({ ...personalInfo, lastName: e.target.value })}
                                            layout="horizontal"
                                        />

                                        {supportedLocales.length > 0 && (
                                            <SelectField
                                                label={t("personalInfo.interfaceLanguage", "Interface language")}
                                                id="locale"
                                                name="locale"
                                                value={personalInfo?.attributes?.locale?.[0] || ""}
                                                options={supportedLocales.map((locale) => ({
                                                    value: locale,
                                                    label: t(`locale_${locale}`, getLanguageDisplayName(locale)),
                                                }))}
                                                placeholder={t("selectOne", "Select an option")}
                                                onChange={(e) => updateFieldValue("locale", e.target.value)}
                                                layout="horizontal"
                                            />
                                        )}
                                    </>
                                )}

                                {/* Action Buttons */}
                                <div className="flex flex-col items-start gap-3 border-t border-border-primary pt-4 md:flex-row md:items-center md:justify-between md:pt-6">
                                    <div className="flex items-center">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            loading={isLoading}
                                            loadingText={t("saving", "Saving...")}
                                            fullWidth={true}
                                            className="sm:w-auto"
                                        >
                                            {t("save", "Save")}
                                        </Button>
                                    </div>

                                    <div className="text-xs text-text-tertiary">
                                        * {t("personalInfo.requiredFields", "Required fields")}
                                    </div>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </Page>
    );
};
